import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, Check, LogOut, Loader, ShieldCheck, AlertTriangle, Ticket } from "lucide-react";
import { useStaffAuth } from "./StaffAuth";
import StaffLogin from "./StaffLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status }) {
  const cls = {
    ACTIVE: "status-ok",
    PENDING: "status-wait",
    REDEEMED: "status-done",
    EXPIRED: "status-cold",
    CANCELLED: "status-cold",
  }[status] || "status-wait";
  return <span className={`status-pill ${cls}`}>{status}</span>;
}

export default function StaffPortal() {
  const { staff, loading, logout, authHeader } = useStaffAuth();
  const [code, setCode] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [searching, setSearching] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const navigate = useNavigate();

  if (loading) return <div className="auth-shell"><div className="auth-card"><p className="muted">Loading…</p></div></div>;
  if (!staff) return <StaffLogin />;

  async function lookup(e) {
    e?.preventDefault();
    setErr("");
    setMsg("");
    setVoucher(null);
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setSearching(true);
    try {
      const r = await axios.get(`${API}/staff/vouchers/${encodeURIComponent(trimmed)}`, { headers: authHeader() });
      setVoucher(r.data.voucher);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not find a voucher with that code.");
    } finally {
      setSearching(false);
    }
  }

  async function redeem() {
    if (!voucher) return;
    setErr("");
    setMsg("");
    setRedeeming(true);
    try {
      const r = await axios.post(
        `${API}/staff/vouchers/${encodeURIComponent(voucher.voucher_code)}/redeem`,
        null,
        { headers: authHeader() },
      );
      setVoucher(r.data.voucher);
      setMsg(`Redeemed. Enjoy the session — ${r.data.voucher.recipient_name || "guest"}'s voucher is now closed.`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not redeem this voucher.");
    } finally {
      setRedeeming(false);
    }
  }

  return (
    <div className="staff-shell" data-testid="staff-portal">
      <header className="staff-header">
        <div>
          <p className="eyebrow"><ShieldCheck size={13} /> Zorba staff</p>
          <h1>Voucher check-in</h1>
          <p className="muted-small">Signed in as <strong>{staff.name}</strong> ({staff.email})</p>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={async () => { await logout(); navigate("/staff/login"); }}
          data-testid="staff-logout"
        >
          <LogOut size={14} /> Sign out
        </button>
      </header>

      <form onSubmit={lookup} className="voucher-lookup" data-testid="voucher-lookup-form">
        <label>
          Voucher code
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="ZORBA-XXXX-XXXX"
            autoFocus
            data-testid="voucher-code-input"
          />
        </label>
        <button type="submit" className="btn btn-primary" disabled={searching || !code.trim()} data-testid="voucher-lookup-submit">
          {searching ? <><Loader size={14} className="spin" /> Looking up…</> : <><Search size={14} /> Look up</>}
        </button>
      </form>

      {err && <p className="form-error" data-testid="staff-error">{err}</p>}
      {msg && <p className="form-success" data-testid="staff-success"><Check size={14} /> {msg}</p>}

      {voucher && (
        <article className="voucher-detail-card" data-testid="voucher-detail-card">
          <header>
            <div>
              <p className="eyebrow"><Ticket size={13} /> Voucher</p>
              <h2>{voucher.service_name}</h2>
              <p className="muted-small">{voucher.duration} · ₹{voucher.amount?.toLocaleString("en-IN")}</p>
            </div>
            <StatusBadge status={voucher.status} />
          </header>
          <dl className="voucher-detail-grid">
            <div><dt>Recipient</dt><dd>{voucher.recipient_name || "—"} · {voucher.recipient_mobile || "—"}</dd></div>
            <div><dt>From</dt><dd>{voucher.sender_name || "—"} · {voucher.sender_contact || "—"}</dd></div>
            <div><dt>Issued</dt><dd>{voucher.issue_date?.slice(0, 10) || "—"}</dd></div>
            <div><dt>Valid until</dt><dd>{voucher.expiry_date?.slice(0, 10) || "—"}</dd></div>
            {voucher.personal_message && (
              <div className="full-row">
                <dt>Message</dt>
                <dd><em>"{voucher.personal_message}"</em></dd>
              </div>
            )}
            {voucher.redeemed_at && (
              <div><dt>Redeemed</dt><dd>{voucher.redeemed_at?.slice(0, 16).replace("T", " ")} by {voucher.redeemed_by}</dd></div>
            )}
          </dl>

          {voucher.status === "ACTIVE" ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={redeem}
              disabled={redeeming}
              data-testid="voucher-redeem-button"
            >
              {redeeming ? <><Loader size={14} className="spin" /> Marking redeemed…</> : <><Check size={14} /> Mark as redeemed</>}
            </button>
          ) : (
            <p className="muted-small">
              <AlertTriangle size={13} /> This voucher cannot be redeemed because it is {voucher.status.toLowerCase()}.
            </p>
          )}
        </article>
      )}
    </div>
  );
}
